import { api } from "./api";
import { formatPrice, platformLabel } from "./format";

/**
 * Everything the Dismissed page does that is not JSX.
 *
 * Same split as `savedPage.ts`, with one thing this page has and that one does
 * not: FILTERING. The days-back window and the only-accidental toggle run here,
 * over the full list the API returns, because the server's accidental verdict
 * has to see every dismissal to decide what was first in its day. Filtering
 * server-side would mean computing over everything and returning a slice
 * anyway, at a round trip per slider drag.
 */

/** Mirrors `DismissedItem` in `server/routers/dismissed.py`. */
export interface DismissedItem {
  key: string;
  title: string;
  artist: string;
  platform: string;
  price: number | null;
  url: string;
  image_url: string;
  dismissed_at: string;
  accidental: boolean;
}

/** Mirrors `DismissedPage`. */
export interface DismissedPayload {
  items: DismissedItem[];
  total_dismissed: number;
}

/** What the sidebar controls, in one object so the caller passes one thing. */
export interface DismissedFilters {
  days: number;
  onlyAccidental: boolean;
}

export const DEFAULT_FILTERS: DismissedFilters = { days: 30, onlyAccidental: false };

/** 7 to 365, in steps of 7. */
export const DAYS_MIN = 7;
export const DAYS_MAX = 365;
export const DAYS_STEP = 7;

/**
 * The API returns still-linkable items only, already newest-first. This does
 * NOT re-sort, for the reason `fetchSaved` gives: two orderings disagree the
 * moment one changes and whichever runs last silently wins.
 */
export async function fetchDismissed(): Promise<DismissedPayload> {
  const res = await api.authedFetch("/api/dismissed");
  if (!res.ok) throw new Error(`Could not load dismissed items (HTTP ${res.status})`);
  return (await res.json()) as DismissedPayload;
}

/**
 * Un-dismiss, by state-file key.
 *
 * `encodeURIComponent` is load-bearing: every key contains a `|`, which is not
 * a legal URL character and is accepted by some stacks and rejected by others.
 *
 * The server does more than delete here -- it re-derives the artist's
 * auto-filter pattern, so the item does not get re-hidden on the next load.
 * That is invisible from this side, which is the point: one call, one action.
 */
export async function undismiss(key: string): Promise<void> {
  const res = await api.authedFetch(`/api/dismissed/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
  if (res.status === 404) {
    throw new Error("That item is no longer dismissed. Reload to see the current list.");
  }
  if (!res.ok) throw new Error(`Could not un-dismiss (HTTP ${res.status})`);
}

/**
 * Is this dismissal inside the window?
 *
 * `now` is a parameter rather than a `Date.now()` call, so the tests pin a
 * fixed instant instead of depending on the day they run. An unparseable or
 * missing timestamp is OUT: a record with no date cannot be shown to be
 * recent, and defaulting it to visible would put the oldest records at the
 * top of a 7-day view.
 */
export function inWindow(item: DismissedItem, days: number, now: Date): boolean {
  if (!item.dismissed_at) return false;
  const at = new Date(item.dismissed_at);
  if (Number.isNaN(at.getTime())) return false;
  return now.getTime() - at.getTime() <= days * 24 * 60 * 60 * 1000;
}

/**
 * Apply both filters.
 *
 * The order does not change the result: they are independent predicates.
 */
export function applyFilters(
  items: DismissedItem[],
  filters: DismissedFilters,
  now: Date,
): DismissedItem[] {
  const windowed = items.filter((item) => inWindow(item, filters.days, now));
  return filters.onlyAccidental ? windowed.filter((item) => item.accidental) : windowed;
}

/**
 * The caption under the title, which reports four numbers.
 *
 * The gap between "still-linkable" and "total dismissed" is the interesting
 * part: it is how much of the dismissed pile has aged out of being recoverable,
 * and it is why the API sends `total_dismissed` separately rather than letting
 * the client count what it received.
 */
export function summaryLine(
  visible: DismissedItem[],
  payload: DismissedPayload,
  days: number,
): string {
  const flagged = visible.filter((item) => item.accidental).length;
  return (
    `${visible.length} still-linkable in last ${days} days ` +
    `(⚠️ ${flagged} possibly accidental) — ` +
    `${payload.items.length} total still-linkable, ${payload.total_dismissed} total dismissed`
  );
}

/** The metadata line under the title: price, then platform. */
export function metaLine(item: DismissedItem): string {
  const price = formatPrice(item.price);
  const platform = platformLabel(item.platform);
  return [price, platform].filter(Boolean).join(" | ");
}

/** Falls back to the key when an item has no title. */
export function displayTitle(item: DismissedItem): string {
  return item.title || item.key;
}
